import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import {
  Loader2, TrendingUp, TrendingDown, AlertTriangle, BadgePoundSterling,
  Percent, ChevronRight, RefreshCw,
} from "lucide-react";
import { fmtMoney } from "./util";
import { shortId } from "./workspace/util";

const RANGES = [
  { k: "today", label: "Today" },
  { k: "week",  label: "This week" },
  { k: "month", label: "This month" },
  { k: "year",  label: "This year" },
];

/* Margin Dashboard. Pure profitability view — revenue, cost, gross profit,
   margin %, plus three quick lists: top, lowest, and loss-making jobs. */
export default function MarginDashboard() {
  const [rng, setRng] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/margin-dashboard", { params: { rng } });
      setData(data);
    } finally { setLoading(false); }
  }, [rng]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-[#475569] flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading…</div>;
  const s = data.series;
  const h = data.headline;

  return (
    <div data-testid="margin-dashboard-page">
      <div className="admin-page-header flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow">Finance · Profitability</p>
          <h1>Margin Dashboard</h1>
          <p className="lead">Live profitability across every job that's billed.</p>
        </div>
        <button onClick={load} className="btn-outline h-10 py-0 px-3" data-testid="margin-refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""}/></button>
      </div>

      {/* Four-window strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="margin-window-strip">
        {RANGES.map((r) => {
          const w = s?.[r.k] || {};
          const active = rng === r.k;
          return (
            <button
              key={r.k}
              onClick={() => setRng(r.k)}
              className={`text-left rounded-xl p-4 border transition ${
                active ? "border-[#064E2E] bg-[#064E2E] text-white shadow-md" : "border-[#E2DCD0] bg-white hover:border-[#064E2E]"
              }`}
              data-testid={`margin-range-${r.k}`}
            >
              <p className={`text-[10px] tracking-[0.22em] uppercase font-semibold ${active ? "text-[#F6C56C]" : "text-[#B88A2A]"}`}>{r.label}</p>
              <p className={`font-display text-2xl tabular-nums mt-1 ${active ? "text-white" : "text-[#071A33]"}`}>{fmtMoney(w.revenue || 0)}</p>
              <p className={`text-[11px] mt-0.5 ${active ? "text-white/70" : "text-[#475569]"}`}>
                <MarginTone value={w.margin_percentage || 0} inverted={active}/>{" · "}{w.jobs || 0} jobs
              </p>
            </button>
          );
        })}
      </div>

      {/* Headline KPIs for the selected window */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="margin-headline">
        <Kpi label="Revenue"          value={fmtMoney(h.revenue)}         icon={<BadgePoundSterling size={14}/>}/>
        <Kpi label="Contractor cost"  value={fmtMoney(h.contractor_cost)} icon={<TrendingDown size={14}/>}/>
        <Kpi label="Gross profit"     value={fmtMoney(h.gross_profit)}    icon={<TrendingUp size={14}/>} tone={h.gross_profit >= 0 ? "green" : "red"}/>
        <Kpi label="Margin"           value={`${h.margin_percentage}%`}    icon={<Percent size={14}/>} tone={marginTone(h.margin_percentage)}/>
      </div>

      {/* Three tables */}
      <div className="grid lg:grid-cols-2 gap-5">
        <Table title="Top margin jobs" subtitle="Most profitable in window" tone="green" rows={data.top_margin}/>
        <Table title="Lowest margin jobs" subtitle="Slimmest profit margin" tone="amber" rows={data.lowest_margin}/>
        <Table title="Loss-making jobs" subtitle="Client price below contractor cost" tone="red" rows={data.loss_making} wide/>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon, tone }) {
  const cls = tone === "green" ? "border-[#064E2E]/30 bg-[#064E2E]/5"
            : tone === "amber" ? "border-[#B88A2A]/30 bg-[#B88A2A]/5"
            : tone === "red"   ? "border-[#B91C1C]/30 bg-[#B91C1C]/5"
            : "border-[#E2DCD0] bg-white";
  return (
    <div className={`border rounded-xl p-4 ${cls}`}>
      <p className="text-[10px] tracking-[0.18em] uppercase text-[#475569] flex items-center gap-1.5">{icon}{label}</p>
      <p className="font-display text-3xl text-[#071A33] tabular-nums mt-1 tracking-tight">{value}</p>
    </div>
  );
}

function Table({ title, subtitle, tone, rows, wide }) {
  const accent = tone === "green" ? "bg-[#064E2E]" : tone === "amber" ? "bg-[#B88A2A]" : "bg-[#B91C1C]";
  return (
    <section className={`admin-panel overflow-hidden ${wide ? "lg:col-span-2" : ""}`} data-testid={`margin-table-${tone}`}>
      <header className={`px-5 py-4 border-b border-[#E2DCD0] bg-gradient-to-b from-[#F7F4ED]/80 to-white flex items-center gap-3`}>
        <span className={`w-2 h-8 rounded-r ${accent}`}/>
        <div>
          <h3 className="font-display text-lg text-[#071A33] tracking-tight">{title}</h3>
          <p className="text-[11px] text-[#475569]">{subtitle}</p>
        </div>
      </header>
      {rows.length === 0 ? (
        <p className="px-4 py-8 text-center text-xs text-[#475569] italic">No jobs in this window.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-[10px] tracking-[0.18em] uppercase text-[#475569] bg-[#F7F4ED]/40">
            <tr className="text-left">
              <th className="px-4 py-2">Job</th>
              <th className="px-4 py-2">Customer · Park</th>
              <th className="px-4 py-2 text-right">Revenue</th>
              <th className="px-4 py-2 text-right">Profit</th>
              <th className="px-4 py-2 text-right">Margin</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2DCD0]">
            {rows.slice(0, 10).map((r) => (
              <tr key={r.case_id} className="hover:bg-[#F7F4ED]/40" data-testid={`margin-row-${r.case_id}`}>
                <td className="px-4 py-2 font-mono text-[#0F766E]"><Link to={`/adminpanel/workspace/${r.case_id}`} className="hover:underline">{shortId(r.case_id)}</Link></td>
                <td className="px-4 py-2 text-[#475569]"><span className="text-[#071A33] font-medium">{r.full_name || "—"}</span>{r.park_name && <span> · {r.park_name}</span>}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(r.revenue)}</td>
                <td className={`px-4 py-2 text-right tabular-nums ${r.gross_profit < 0 ? "text-[#B91C1C] font-semibold" : "text-[#071A33]"}`}>{fmtMoney(r.gross_profit)}</td>
                <td className="px-4 py-2 text-right tabular-nums"><MarginTone value={r.margin_percentage}/></td>
                <td className="px-4 py-2 text-right"><Link to={`/adminpanel/workspace/${r.case_id}`} className="text-[#0F766E] text-[12px] inline-flex items-center gap-1 hover:underline">Open <ChevronRight size={11}/></Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function MarginTone({ value, inverted }) {
  const tone = marginTone(value);
  const cls = tone === "green" ? (inverted ? "text-[#5EEAD4]" : "text-[#064E2E]")
            : tone === "amber" ? (inverted ? "text-[#F6C56C]" : "text-[#B88A2A]")
            : tone === "red"   ? (inverted ? "text-[#FCA5A5]" : "text-[#B91C1C]")
            : "";
  return <span className={`font-semibold ${cls} tabular-nums`}>{value?.toFixed ? value.toFixed(2) : value}%</span>;
}

function marginTone(v) {
  const n = Number(v) || 0;
  if (n >= 30) return "green";
  if (n >= 15) return "amber";
  return "red";
}
